import {
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import type { Crumb } from '../lib/breadcrumbs';
import { BreadcrumbFocusContext, type PendingBreadcrumbFocus } from '../contexts/breadcrumbFocusContextValues';
import './Breadcrumbs.css';

export interface BreadcrumbsProps {
  crumbs: Crumb[];
  casePath?: string;
  className?: string;
}

export function Breadcrumbs({ crumbs, casePath, className }: BreadcrumbsProps) {
  const navRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const expandRef = useRef<HTMLButtonElement>(null);
  const sharedFocus = useContext(BreadcrumbFocusContext);
  const localFocus = useRef<PendingBreadcrumbFocus | null>(null);
  const focusHandoff = sharedFocus ?? localFocus;
  const [expanded, setExpanded] = useState(false);
  const fullTrailId = useId();
  const hasCrumbs = crumbs.length > 0;
  const hasRoot = crumbs[0]?.path === '/cases';
  const hasCase = hasRoot && casePath !== undefined && crumbs[1]?.path === casePath;
  const trailKey = JSON.stringify([crumbs, casePath]);

  const reveal = useCallback((target: Element | null) => {
    const list = listRef.current;
    if (!list || !(target instanceof HTMLElement) || !list.contains(target)) return;
    const item = target.closest('li');
    if (!item || item.parentElement !== list) return;
    if (item.hasAttribute('data-pin') || item.hasAttribute('data-current-anchor')) return;
    const bounds = list.getBoundingClientRect();
    const pinnedWidth = Array.from(list.children).reduce((sum, child) =>
      sum + (child.hasAttribute('data-pin') ? child.getBoundingClientRect().width : 0), 0);
    const targetBounds = target.getBoundingClientRect();
    const left = bounds.left + pinnedWidth + 2;
    const currentWidth = list.querySelector('[data-current-anchor="true"]')?.getBoundingClientRect().width ?? 0;
    const right = bounds.right - currentWidth - 2;
    if (targetBounds.left < left) list.scrollLeft -= left - targetBounds.left;
    else if (targetBounds.right > right) list.scrollLeft += targetBounds.right - right;
  }, []);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    // Keep the compatibility fallback only on engines without container units.
    const needsMeasuredWidth = typeof CSS === 'undefined' || typeof CSS.supports !== 'function' || !CSS.supports('width', '1cqi');
    let disposed = false;
    const sync = () => {
      if (disposed) return;
      if (needsMeasuredWidth) {
        const width = `${nav.getBoundingClientRect().width}px`;
        if (nav.style.getPropertyValue('--measured-width') !== width) {
          nav.style.setProperty('--measured-width', width);
        }
      }
      updateTitles(nav);
      reveal(document.activeElement);
    };
    sync();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
    observer?.observe(nav);
    // Font metrics can change truncation without resizing the navigation box.
    const fonts = document.fonts;
    fonts?.addEventListener('loadingdone', sync);
    return () => {
      disposed = true;
      observer?.disconnect();
      fonts?.removeEventListener('loadingdone', sync);
    };
  }, [reveal, hasCrumbs]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (navRef.current) updateTitles(navRef.current);
    reveal(document.activeElement);
  }, [trailKey, reveal]);

  useLayoutEffect(() => {
    const pending = focusHandoff.current;
    const nav = navRef.current;
    if (!pending || !nav) return;
    focusHandoff.current = null;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active !== document.body &&
      active !== pending.element &&
      active.isConnected
    ) return;
    const labels = Array.from(
      nav.querySelectorAll<HTMLElement>('.breadcrumbs__list .breadcrumbs__label'),
    );
    const same = pending.path === null
      ? undefined
      : labels.find(label => label.dataset.crumbPath === pending.path);
    const current = labels.find(label => label.getAttribute('aria-current') === 'page');
    const caseLabel = labels.find(
      label => casePath !== undefined && label.dataset.crumbPath === casePath,
    );
    const target = same ?? current ?? caseLabel ?? labels.find(label => label instanceof HTMLAnchorElement);
    target?.focus({ preventScroll: true });
    reveal(target ?? null);
  });

  if (!hasCrumbs) return null;
  return (
    <nav
      ref={navRef}
      aria-label="Breadcrumb"
      dir="ltr"
      className={['breadcrumbs', className].filter(Boolean).join(' ')}
      data-has-root={hasRoot || undefined}
      data-has-case={hasCase || undefined}
    >
      <ol ref={listRef} className="breadcrumbs__list" aria-label="Breadcrumb trail" onFocusCapture={event => reveal(event.target)}>
        {crumbs.map((crumb, index) => (
          <li
            key={crumb.path ?? `ctx:${index}`}
            data-pin={hasRoot && index === 0 ? 'root' : hasCase && index === 1 ? 'case' : undefined}
            data-current-anchor={crumb.current && index > (hasCase ? 1 : hasRoot ? 0 : -1) ? 'true' : undefined}
          >
            {index > 0 && <ChevronRightIcon className="breadcrumbs__separator" aria-hidden="true" focusable="false" />}
            <CrumbLabel crumb={crumb} focusHandoff={focusHandoff} />
          </li>
        ))}
      </ol>
      <button
        ref={expandRef}
        type="button"
        className="breadcrumbs__expand"
        aria-label={expanded ? 'Hide full breadcrumb trail' : 'Show full breadcrumb trail'}
        aria-expanded={expanded}
        aria-controls={fullTrailId}
        onClick={() => setExpanded(value => !value)}
      >…</button>
      {expanded && <div
        id={fullTrailId}
        className="breadcrumbs__full"
        role="group"
        aria-label="Full breadcrumb trail"
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.preventDefault();
            // This disclosure is the topmost dismissible layer; keep outer window/document Escape owners from closing what is underneath.
            event.stopPropagation();
            setExpanded(false);
            expandRef.current?.focus({ preventScroll: true });
          }
        }}
      >
        <ol>
          {crumbs.map((crumb, index) => <li key={crumb.path ?? `full:${index}`}>
            {crumb.path && !crumb.current
              ? <FullTrailLink
                  crumb={{ ...crumb, path: crumb.path }}
                  focusHandoff={focusHandoff}
                  onNavigate={() => setExpanded(false)}
                />
              : <span><bdi dir="auto">{crumb.label}</bdi>{crumb.current ? ' (current page)' : ''}</span>}
          </li>)}
        </ol>
      </div>}
    </nav>
  );
}


function CrumbLabel({
  crumb,
  focusHandoff,
}: {
  crumb: Crumb;
  focusHandoff: MutableRefObject<PendingBreadcrumbFocus | null>;
}) {
  const labelRef = useRef<HTMLElement | null>(null);
  const attachLabel = useCallback((node: HTMLElement | null) => {
    if (!node && labelRef.current === document.activeElement && labelRef.current) {
      focusHandoff.current = { path: crumb.path ?? null, element: labelRef.current };
    }
    labelRef.current = node;
  }, [crumb.path, focusHandoff]);
  return crumb.path !== undefined && !crumb.current ? (
    <Link ref={attachLabel} className="breadcrumbs__label" data-crumb-path={crumb.path} to={crumb.path}>
      <bdi dir="auto">{crumb.label}</bdi>
    </Link>
  ) : (
    <span ref={attachLabel} className="breadcrumbs__label"
      data-crumb-path={crumb.path}
      aria-current={crumb.current ? 'page' : undefined} tabIndex={crumb.current ? 0 : undefined}>
      <bdi dir="auto">{crumb.label}</bdi>
    </span>
  );
}

function FullTrailLink({
  crumb,
  focusHandoff,
  onNavigate,
}: {
  crumb: Crumb & { path: string };
  focusHandoff: MutableRefObject<PendingBreadcrumbFocus | null>;
  onNavigate: () => void;
}) {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const attach = useCallback((node: HTMLAnchorElement | null) => {
    if (!node && ref.current === document.activeElement && ref.current) {
      focusHandoff.current = { path: crumb.path, element: ref.current };
    }
    ref.current = node;
  }, [crumb.path, focusHandoff]);
  return (
    <Link
      ref={attach}
      to={crumb.path}
      onClick={event => {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) return;
        if (ref.current === document.activeElement && ref.current) {
          focusHandoff.current = { path: crumb.path, element: ref.current };
        }
        onNavigate();
      }}
    >
      <bdi dir="auto">{crumb.label}</bdi>
    </Link>
  );
}


function updateTitles(nav: HTMLElement) {
  nav.querySelectorAll<HTMLElement>('.breadcrumbs__label').forEach(label => {
    if (label.scrollWidth > label.clientWidth) label.title = label.textContent ?? '';
    else label.removeAttribute('title');
  });
}
